import { isAffirmative } from '../../prix'

declare const DocumentTouch: Function

// export const polyfill =
//   'https://polyfill.io/v3/polyfill.min.js?features=RegExp.prototype.flags%2Cdefault%2Ces2015%2Ces2016%2Ces2017%2Ces2018%2Ces2019%2Cblissfuljs%2CResizeObserver%2CMap%2CSet%2CAbortController%2CIntersectionObserver%2CIntersectionObserverEntry%2CString.prototype.padEnd%2CString.prototype.padStart%2CString.prototype.normalize'

export const apiBasePath = '/api/v1'

export const stagingHostnames: string[] = ['staging.prix.app']
export const productionHostnames: string[] = ['prix.app', 'prixapp.com.br', 'prixapp.com']
export const partnersHostnames: string[] = [
  'segurancapublica.prix.app',
  'stagingsegurancapublica.prix.app',
  'saudepublica.prix.app',
  'agricultura.prix.app',
  'stagingagricultura.prix.app',
]
export const allowedAPIHostnames = [
  ...stagingHostnames,
  ...productionHostnames,
  ...partnersHostnames,
]
export const apiCorsAllowedHeaders = [
  'Content-Type',
  'Authorization',
  'X-Requested-With',
  'App-Version',
  'Token',
  'X-Prix-Active-Client-Id',
  'X-API-Key',
]

export const siteHostnames = [...stagingHostnames, ...productionHostnames]

export const networkBindingHostname = '0.0.0.0'
export const localHostname = 'localhost'
export const localPort = String(
  typeof process !== 'undefined'
    ? process.env.PRIX_GOV_WEB_APP_PORT || 3000
    : typeof window !== 'undefined'
      ? window.location.port
      : '',
)
export const localHost = `http://${localHostname}${localPort ? `:${localPort}` : ''}`

export type Environment =
  | 'development'
  | 'production'
  | 'staging'
  | 'prix-gov-production'
  | 'prix-gov-staging'
export const environmentOnBackend = (
  typeof process !== 'undefined'
    ? process.env.ENVIRONMENT || process.env.PRIX_GOV_DISTRIBUTION_ENVIRONMENT || null
    : null
) as Environment | null
export const environmentOnFrontend = (() => {
  if (typeof window === 'undefined') {
    return null
  }
  if (window.location.hostname === localHostname) {
    return 'development'
  }
  if (stagingHostnames.includes(window.location.hostname)) {
    return 'staging'
  }
  if (productionHostnames.includes(window.location.hostname)) {
    return 'production'
  }
  return null
})()
export const environment = environmentOnBackend || environmentOnFrontend || 'development'

export function resolvePrimarySiteHostname(environmentName: Environment | null = environment) {
  if (environmentName === 'staging' || environmentName === 'prix-gov-staging') {
    return stagingHostnames[0]
  }
  return productionHostnames[0]
}

export const primarySiteHost = `https://${resolvePrimarySiteHostname()}`

export function isProductionOrStagingEnvironment({
  environmentName = environment,
  nodeEnvironment = typeof process !== 'undefined' ? process.env.NODE_ENV : undefined,
  projectId = typeof process !== 'undefined' ? process.env.GCP_PROJECT_ID : undefined,
}: {
  environmentName?: string | null
  nodeEnvironment?: string | null
  projectId?: string | null
} = {}) {
  return (
    environmentName === 'production' ||
    environmentName === 'staging' ||
    environmentName === 'prix-gov-production' ||
    environmentName === 'prix-gov-staging' ||
    projectId === 'prix-gov-production' ||
    projectId === 'prix-gov-staging' ||
    nodeEnvironment === 'production'
  )
}

export const isProductionOrStaging = isProductionOrStagingEnvironment()

function checkIsTouchDevice() {
  if (typeof window === 'undefined') {
    return false
  }
  if ((window as any).DocumentTouch && document instanceof DocumentTouch) {
    return true
  }

  if (!window.matchMedia) {
    return false
  }

  const prefixes = ['', '-webkit-', '-moz-', '-o-', '-ms-']
  const queries = prefixes.map(prefix => `(${prefix}touch-enabled)`)

  return window.matchMedia(queries.join(',')).matches
}

export const isTouchDevice = checkIsTouchDevice()

export const isServiceWorkerSupported =
  typeof window !== 'undefined' && window.navigator && 'serviceWorker' in window.navigator

export const NavigatorStorageSupported =
  typeof window !== 'undefined' && navigator.serviceWorker !== undefined

export const isDevelopment = process.env.NODE_ENV === 'development'

export function isDevelopmentEnvironment({
  environmentName = environment,
  nodeEnvironment = typeof process !== 'undefined' ? process.env.NODE_ENV : undefined,
  projectId = typeof process !== 'undefined' ? process.env.GCP_PROJECT_ID : undefined,
}: {
  environmentName?: string | null
  nodeEnvironment?: string | null
  projectId?: string | null
} = {}) {
  if (
    isProductionOrStagingEnvironment({
      environmentName,
      nodeEnvironment,
      projectId,
    })
  ) {
    return false
  }

  return environmentName === 'development' || nodeEnvironment === 'development'
}

function normalizeHostnameForAPIComparison(hostname: string) {
  return hostname.trim().toLowerCase().replace(/^\[/, '').replace(/\]$/, '')
}

function parseIPv4Address(hostname: string) {
  const addressParts = hostname.split('.')

  if (addressParts.length !== 4) {
    return null
  }

  const parsedAddressParts = addressParts.map(addressPart => {
    if (!/^\d+$/.test(addressPart)) {
      return null
    }

    const numericAddressPart = Number(addressPart)

    return numericAddressPart >= 0 && numericAddressPart <= 255 ? numericAddressPart : null
  })

  if (parsedAddressParts.some(addressPart => addressPart === null)) {
    return null
  }

  return parsedAddressParts as [number, number, number, number]
}

function isLocalOrPrivateDevelopmentHostname(hostname: string) {
  const normalizedHostname = normalizeHostnameForAPIComparison(hostname)

  if (['localhost', '127.0.0.1', '0.0.0.0', '::1'].includes(normalizedHostname)) {
    return true
  }

  const parsedIPv4Address = parseIPv4Address(normalizedHostname)

  if (!parsedIPv4Address) {
    return false
  }

  const [firstAddressPart, secondAddressPart] = parsedIPv4Address

  return (
    firstAddressPart === 10 ||
    (firstAddressPart === 172 && secondAddressPart >= 16 && secondAddressPart <= 31) ||
    (firstAddressPart === 192 && secondAddressPart === 168)
  )
}

export function isAllowedAPIHostname(
  hostname: string,
  options: {
    environmentName?: string | null
    nodeEnvironment?: string | null
    projectId?: string | null
  } = {},
) {
  const normalizedHostname = normalizeHostnameForAPIComparison(hostname)

  if (allowedAPIHostnames.includes(normalizedHostname)) {
    return true
  }

  return isDevelopmentEnvironment(options) && isLocalOrPrivateDevelopmentHostname(normalizedHostname)
}

function getFirstHeaderValue(headerValue: string | string[] | undefined) {
  return Array.isArray(headerValue) ? headerValue[0] : headerValue
}

export function getAPIRequestOrigin(headerValue: string | string[] | undefined) {
  const firstHeaderValue = getFirstHeaderValue(headerValue)

  if (!firstHeaderValue) {
    return null
  }

  try {
    return new URL(firstHeaderValue).origin
  } catch {
    return null
  }
}

export function resolveAllowedAPIOrigin({
  origin,
  referer,
  fallbackOrigin,
  environmentName,
  nodeEnvironment,
  projectId,
}: {
  origin?: string | string[]
  referer?: string | string[]
  fallbackOrigin: string
  environmentName?: string | null
  nodeEnvironment?: string | null
  projectId?: string | null
}) {
  const requestOrigin = getAPIRequestOrigin(origin) ?? getAPIRequestOrigin(referer)

  if (!requestOrigin) {
    return fallbackOrigin
  }

  const requestURL = new URL(requestOrigin)

  if (
    isAllowedAPIHostname(requestURL.hostname, {
      environmentName,
      nodeEnvironment,
      projectId,
    })
  ) {
    return requestOrigin
  }

  return fallbackOrigin
}

export const bypassAuth =
  isAffirmative(process.env.BYPASS_AUTH) || process.argv?.includes('--bypass-auth')

export const isCloudRunContainer = Boolean(
  (process.env.K_SERVICE || process.env.K_REVISION || process.env.K_CONFIGURATION) &&
  !process.env.FUNCTION_NAME &&
  !process.env.FUNCTION_TARGET,
)

export const isCloudFunction =
  Boolean(process.env.FUNCTION_NAME || process.env.FUNCTION_TARGET || process.env.K_SERVICE) &&
  !isCloudRunContainer

export const isLongAction = Boolean(process.env.INPUT || process.env.CONTEXT)
